import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { FormErrorsComponent } from 'src/app/components/form-errors/form-errors.component';
import { DropdownComponent } from 'src/app/components/shared/dropdown/dropdown.component';
import { AddUser } from 'src/app/models/security/add-user';
import {
  AddAddress,
  RegisterExtendedData,
} from 'src/app/models/security/register-request';
import { UpdateUser } from 'src/app/models/security/update-user';
import { UserRoleIds } from 'src/app/models/security/user-role-enum';
import {
  DropdownOption,
  DropDownKeywords,
} from 'src/app/models/shared/dropdown-option';
import { AlertService } from 'src/app/services/alert.service';
import { DropdownService } from 'src/app/services/dropdown.service';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { UserService } from 'src/app/services/user.service';
import {
  passwordValidator,
  matchPasswords,
} from 'src/app/validators/password-validation';
import { firstValueFrom } from 'rxjs';
import { MAX_FILE_SIZE } from 'src/app/constants/file.constants';

@Component({
  selector: 'app-users-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormErrorsComponent,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    DropdownComponent,
    NgxMaskDirective,
  ],
  templateUrl: './users-modal.component.html',
  styleUrls: ['./users-modal.component.css'],
  providers: [provideNgxMask()],
})
export class UsersModalComponent implements OnInit {
  // Variables y propiedades de clase
  form: FormGroup = new FormGroup({});
  isLoading = false;
  isSaving = false;
  // Listas para dropdowns
  typeIdentList: DropdownOption[] = [];
  nationalityList: DropdownOption[] = [];
  sexList: DropdownOption[] = [];
  // Para previsualización de la foto
  photoPreview: string | null = null;
  identificationDocPreview: string | null = null;
  // Variables para manejo local de archivos
  selectedPhotoFile: File | null = null;
  selectedIdentificationDocFile: File | null = null;
  // Constantes
  readonly ADMIN_ROLE_ID = UserRoleIds.Admin;
  readonly minAge = 18; // Edad mínima requerida
  // Máscara para documento de identidad
  documentMask = '00000000000';
  // Mapeo de tipos de identificación a máscaras
  private documentMasks: { [key: number]: string } = {
    1: '000000000', // Cédula
    2: '00000000000', // RNC
    3: '000000000000000', // Pasaporte
    4: '000000000', // Otro
  };
  // Arreglo vacío de direcciones (requerido por el backend)
  addresses: AddAddress[] = [];

  // Constructor
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UsersModalComponent>,
    private userService: UserService,
    private alertService: AlertService,
    private toastService: ToastrService,
    private dropdownService: DropdownService,
    private fileStorage: FileStorageService,
    private datePipe: DatePipe,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.buildForm();
    this.loadDropdowns().then(() => {
      if (this.data?.item) {
        this.form.patchValue({ ...this.data?.item });
        // Cargar previsualizaciones si existen
        if (this.data.item.photo) {
          this.photoPreview = this.fileStorage.getFileUrl(this.data.item.photo);
        }
        if (this.data.item.identificationDocument) {
          this.identificationDocPreview = this.fileStorage.getFileUrl(
            this.data.item.identificationDocument
          );
        }
      }
      if (this.data?.view) {
        this.form.disable();
      }
    });
  }

  // Métodos y funciones
  // Manejo de foto de perfil
  onPhotoSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }
    if (!this.validatePhotoFileType(file)) {
      this.alertService.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes JPG o PNG para la foto de perfil'
      );
      input.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.alertService.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }

    this.selectedPhotoFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.photoPreview = e.target.result;
    };
    reader.readAsDataURL(file);
    input.value = '';
    // No subir aquí, solo guardar localmente
  }

  removePhoto() {
    this.selectedPhotoFile = null;
    this.photoPreview = null;
    this.form.patchValue({ photoFileId: null });
  }

  // Manejo de documento de identificación
  onIdentificationDocSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }
    if (!this.validateDocumentFileType(file)) {
      this.alertService.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes JPG, PNG o documentos PDF'
      );
      input.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.alertService.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }
    this.selectedIdentificationDocFile = file;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.identificationDocPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      this.identificationDocPreview = 'assets/Images/pdf-icon.png';
    }
    input.value = '';
    // No subir aquí, solo guardar localmente
  }

  removeIdentificationDoc() {
    this.selectedIdentificationDocFile = null;
    this.identificationDocPreview = null;
    this.form.patchValue({ cedulaFileId: null });
  }

  ngOnInit(): void {
    // Ya se inicializa todo en el constructor
  }

  async loadDropdowns(): Promise<void> {
    try {
      // Cargar tipos de identificación, nacionalidades y sexos
      const [typeIdentResponse, nationalityResponse, sexResponse] =
        await Promise.all([
          firstValueFrom(
            this.dropdownService.GetOptions(DropDownKeywords.TypeIdentification)
          ),
          firstValueFrom(
            this.dropdownService.GetOptions(DropDownKeywords.NationalityId)
          ),
          firstValueFrom(this.dropdownService.GetOptions(DropDownKeywords.Sex)),
        ]);

      this.typeIdentList = typeIdentResponse || [];
      this.nationalityList = nationalityResponse || [];
      this.sexList = sexResponse || [];
    } catch (error) {
      console.error('Error loading dropdowns:', error);
      this.toastService.error('Error al cargar los datos necesarios');
    }
  }

  private buildForm() {
    // Solo creamos administradores
    this.form = this.fb.group(
      {
        // Campos de usuario (obligatorios)
        userId: [0],
        userName: [
          '',
          [Validators.required, Validators.email, Validators.maxLength(100)],
        ],
        firstName: ['', [Validators.required, Validators.maxLength(100)]],
        middleName: ['', [Validators.maxLength(100)]],
        lastName: ['', [Validators.required, Validators.maxLength(100)]],
        secondLastName: ['', [Validators.maxLength(100)]],
        email: [
          '',
          [Validators.required, Validators.email, Validators.maxLength(100)],
        ],
        typeIdentificationId: ['', [Validators.required]],
        identification: ['', [Validators.required, Validators.maxLength(20)]],
        phoneNumber: ['', [Validators.required]], // Teléfono obligatorio
        birthDate: [
          null,
          [Validators.required, this.minAgeValidator(this.minAge)],
        ], // Fecha de nacimiento obligatoria
        nationalityId: ['', [Validators.required]], // Nacionalidad obligatoria
        sexId: ['', [Validators.required]], // Sexo obligatorio
        password: ['', [Validators.required, passwordValidator]],
        confirmPassword: ['', [Validators.required]],

        // Campos para archivos
        photoFileId: [null], // Foto de perfil
        cedulaFileId: [null], // Documento de identidad

        // Campos con valores predeterminados para administradores
        privacityTermsAccepted: [true], // Automáticamente aceptado para admin
        refundTermsAccepted: [true], // Automáticamente aceptado para admin
        roleId: [this.ADMIN_ROLE_ID], // Siempre administrador
        changePassword: [false], // No necesita cambiar contraseña al inicio
      },
      { validators: matchPasswords('password', 'confirmPassword') }
    );

    // Sincronizar userName con email (nombre de usuario es el email)
    this.form.get('email')?.valueChanges.subscribe((value) => {
      this.form.get('userName')?.setValue(value);
    });

    // Establecer la máscara inicial según el tipo de identificación
    this.updateDocumentMask();

    // Actualizar la máscara cuando cambie el tipo de identificación
    this.form.get('typeIdentificationId')?.valueChanges.subscribe(() => {
      this.updateDocumentMask();
    });
  }

  // Método para abrir el selector de archivos
  uploadFoto() {
    document.getElementById('fileFoto')?.click();
  }

  uploadIdentificationDoc() {
    document.getElementById('fileDocReq')?.click();
  }

  // Validación específica para fotos de perfil (solo JPG y PNG)
  validatePhotoFileType(file: File): boolean {
    const allowedPhotoTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    return allowedPhotoTypes.includes(file.type);
  }

  // Validación para documentos de identificación (imágenes y PDF)
  validateDocumentFileType(file: File): boolean {
    const allowedDocumentTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/pdf',
    ];
    return allowedDocumentTypes.includes(file.type);
  }

  // Método para obtener el nombre del documento según el tipo de identificación seleccionado
  getIdentificationDocumentName(): string {
    const typeIdControl = this.form.get('typeIdentificationId');
    if (!typeIdControl || !typeIdControl.value) return 'Documento de identidad';

    const selectedTypeId = typeIdControl.value;
    const selectedType = this.typeIdentList.find((type) => {
      return type.id === selectedTypeId || type.id === +selectedTypeId;
    });
    return selectedType ? selectedType.text : 'Documento de identidad';
  }

  // Método para actualizar la máscara del documento según el tipo de identificación
  updateDocumentMask(): void {
    const typeIdControl = this.form.get('typeIdentificationId');
    if (!typeIdControl || !typeIdControl.value) {
      this.documentMask = '00000000000'; // Máscara por defecto
      return;
    }

    const selectedTypeId = +typeIdControl.value;
    this.documentMask = this.documentMasks[selectedTypeId] || '00000000000';

    // Resetear el campo de identificación cuando cambia el tipo
    const identControl = this.form.get('identification');
    if (identControl) {
      identControl.setValue('');
    }
  }

  getMissingRequiredFields(form: FormGroup): string[] {
    const missingFields: string[] = [];
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control?.errors?.['required'] && (control.touched || control.dirty)) {
        missingFields.push(key);
      }
    });
    return missingFields;
  }

  // Al guardar
  async accept() {
    if (this.form.invalid) {
      const missingFields = this.getMissingRequiredFields(this.form);
      this.alertService.showWarningAlert(
        'Verifique los campos del formulario',
        'Debe completar los campos obligatorios'
      );
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.value;

    if (!v.identification || v.identification?.length < 6) {
      this.alertService.showWarningAlert(
        'Verifique el número de identificación',
        'El número de identificación no es válido'
      );
      return;
    }

    // Prevenir múltiples envíos
    if (this.isSaving) {
      return;
    }

    // Subir archivos SOLO si hay archivos seleccionados localmente
    let photoFileId = v.photoFileId;
    let cedulaFileId = v.cedulaFileId;

    this.isSaving = true;
    this.isLoading = true;
    try {
      if (this.selectedPhotoFile) {
        const res = await firstValueFrom(
          this.fileStorage.uploadFile(this.selectedPhotoFile)
        );
        if (res && res.fileId) {
          photoFileId = res.fileId;
        }
      }
      if (this.selectedIdentificationDocFile) {
        const res = await firstValueFrom(
          this.fileStorage.uploadFile(this.selectedIdentificationDocFile)
        );
        if (res && res.fileId) {
          cedulaFileId = res.fileId;
        }
      }

      // Validar que haya foto de perfil
      if (!photoFileId) {
        this.alertService.showWarningAlert(
          'Foto requerida',
          'Debe subir una foto de perfil para crear el usuario administrador'
        );
        this.isLoading = false;
        this.isSaving = false;
        return;
      }

      // Preparar el objeto de usuario administrador siguiendo el mismo modelo que RegisterRequest
      const user: AddUser = {
        userId: 0,
        userName: v.email,
        firstName: v.firstName,
        middleName: v.middleName,
        lastName: v.lastName,
        secondLastName: v.secondLastName,
        email: v.email,
        typeIdentificationId: +v.typeIdentificationId,
        identification: v.identification,
        roleId: this.ADMIN_ROLE_ID,
        phoneNumber: v.phoneNumber,
        password: v.password,
        changePassword: false,
        privacityTermsAccepted: true,
        refundTermsAccepted: true,
        extendedData: {
          personId: 0,
          photoFileId: photoFileId,
          cedulaFileId: cedulaFileId,
          primaryPhone: v.phoneNumber,
          nationalityId: +v.nationalityId,
          sexId: +v.sexId,
          professionalProfile: '',
          passportFileId: cedulaFileId,
          wantToSearchJobs: false,
          isIndependentProfessional: false,
          publicResumeId: undefined,
          professionalProfileId: undefined,
          wantToPublishOffers: false,
          birthDate: v.birthDate
            ? this.datePipe.transform(v.birthDate, 'yyyy-MM-dd') || ''
            : '',
          primaryPhoneCountryCode: undefined,
          primaryPhoneISO2: undefined,
          primaryPhoneE164: undefined,
          primaryPhoneDialCode: undefined,
          cellPhone: undefined,
          cellPhoneCountryCode: undefined,
          cellPhoneISO2: undefined,
          cellPhoneE164: undefined,
          cellPhoneDialCode: undefined,
          cellPhoneHasWhatsapp: false,
        },
        addresses: this.addresses,
      };

      this.userService.addUser(user).subscribe({
        next: (response) => {
          this.closeDialog(true);
          this.isLoading = false;
          this.isSaving = false;
          this.alertService.showSuccessAlert(
            'Usuario administrador creado',
            'El usuario administrador se ha creado correctamente'
          );
        },
        error: (error) => {
          console.error('Error al crear el usuario administrador:', error);

          // Manejar diferentes formatos de error
          let errorMessage = 'No se pudo crear el usuario administrador';

          // Formato específico mostrado en la imagen
          if (Array.isArray(error)) {
            errorMessage = error[0] || errorMessage;
          }
          // Si el error es un objeto con exceptions
          else if (error?.exceptions && Array.isArray(error.exceptions)) {
            errorMessage = error.exceptions[0] || errorMessage;
          }
          // Si el error viene en formato de error.error.exceptions
          else if (
            error?.error?.exceptions &&
            Array.isArray(error.error.exceptions)
          ) {
            errorMessage = error.error.exceptions[0] || errorMessage;
          }
          // Si el error viene como mensaje directo
          else if (error?.error?.message) {
            errorMessage = error.error.message;
          }
          // Si el error es un string
          else if (typeof error?.error === 'string') {
            errorMessage = error.error;
          }
          // Si el error es un objeto con mensaje
          else if (error?.message) {
            errorMessage = error.message;
          }

          this.alertService.showWarningAlert('Error', errorMessage);
          this.isLoading = false;
          this.isSaving = false;
        },
      });
    } catch (error) {
      console.error('Error inesperado:', error);
      this.alertService.showWarningAlert(
        'Error',
        'Ocurrió un error inesperado. Por favor, inténtelo de nuevo más tarde.'
      );
      this.isLoading = false;
      this.isSaving = false;
    }
  }

  // Validador de edad mínima
  minAgeValidator(minAge: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Si no hay valor, otros validadores se encargarán
      }

      const today = new Date();
      const birthDate = new Date(control.value);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age < minAge
        ? { minAge: { requiredAge: minAge, actualAge: age } }
        : null;
    };
  }

  // Método para actualizar la máscara del documento según el tipo seleccionado
  onDocumentTypeChange(): void {
    const identificationtype = this.form.get('typeIdentificationId')?.value;
    if (identificationtype) {
      const found = this.typeIdentList.find(
        (c) => c.id === +identificationtype
      );
      const relatedId = found?.relatedId ?? 0;
      // Si relatedId = 1, permitir alfanumérico (combinación de letras y números) sin máscara; si no, solo números
      this.documentMask = relatedId === 1 ? '' : '00000000000';
    } else {
      this.documentMask = '00000000000'; // Valor por defecto
    }
  }

  closeDialog(result: boolean = true): void {
    this.isLoading = false;
    this.dialogRef.close(result);
  }
}
